/**
 * MiCoach · Pizarra táctica — utilidades de dibujo y catálogo de herramientas.
 *
 * Toda la lógica pura de la pizarra (constantes, helpers de dibujo, opciones)
 * vive aquí para que pueda ser compartida entre el editor (BoardCanvas)
 * y la página contenedora (TacticalBoardPage), así como por cualquier otro
 * lugar que necesite render del campo (export, preview, etc.).
 *
 * No depende de React. No mantiene estado. Solo funciones puras + datos.
 */

// ============================================================
// TOOL DEFINITIONS
// ============================================================
// Catálogo de herramientas alineado con el documento funcional v4.
// Grupos: Jugadores · Equipamiento · Portería · Movimientos · Delimitadores · Técnicos.
const TOOLS = [
  // Jugadores — los círculos numerados se autoincrementan por equipo (1..n)
  { id: 'player_home', group: 'Jugadores', label: 'Local', color: 'var(--primary)', shape: 'circle', fill: true },
  { id: 'player_away', group: 'Jugadores', label: 'Visitante', color: '#ff4d6d', shape: 'circle', fill: true },
  { id: 'player_neutral', group: 'Jugadores', label: 'Neutro', color: '#ffd34d', shape: 'circle', fill: true },

  // Equipamiento
  { id: 'ball', group: 'Equipamiento', label: 'Balón', color: '#ffffff', shape: 'ball' },
  { id: 'cone', group: 'Equipamiento', label: 'Cono naranja', color: '#ff8c00', shape: 'cone' },
  { id: 'cone_red', group: 'Equipamiento', label: 'Cono rojo', color: '#ff4d6d', shape: 'cone' },
  { id: 'cone_yellow', group: 'Equipamiento', label: 'Cono amarillo', color: '#ffd34d', shape: 'cone' },
  { id: 'cone_blue', group: 'Equipamiento', label: 'Cono azul', color: '#4dd6ff', shape: 'cone' },
  { id: 'cone_green', group: 'Equipamiento', label: 'Cono verde', color: '#10B981', shape: 'cone' },
  { id: 'pole', group: 'Equipamiento', label: 'Pica', color: '#ffffff', shape: 'pole' },
  { id: 'bib_red', group: 'Equipamiento', label: 'Peto rojo', color: '#ff4d6d', shape: 'bib' },
  { id: 'bib_blue', group: 'Equipamiento', label: 'Peto azul', color: '#4dd6ff', shape: 'bib' },
  { id: 'bib_yellow', group: 'Equipamiento', label: 'Peto amarillo', color: '#ffd34d', shape: 'bib' },
  { id: 'mannequin', group: 'Equipamiento', label: 'Maniquí', color: '#a78bfa', shape: 'mannequin' },

  // Portería
  { id: 'goal_big', group: 'Porteria', label: 'Portería grande', color: '#ffffff', shape: 'goal_big' },
  { id: 'goal_small', group: 'Porteria', label: 'Portería mini', color: '#ffd34d', shape: 'goal_small' },

  // Movimientos — 4 tipos diferenciados visualmente
  { id: 'arrow_pass', group: 'Movimientos', label: 'Pase', color: '#ffffff', shape: 'arrow' },
  { id: 'arrow_dribble', group: 'Movimientos', label: 'Conducción', color: '#ffd34d', shape: 'arrow_dribble' },
  { id: 'arrow_run', group: 'Movimientos', label: 'Desmarque', color: '#a78bfa', shape: 'arrow_dashed' },
  { id: 'arrow_shot', group: 'Movimientos', label: 'Tiro', color: '#ff4d6d', shape: 'arrow_shot' },

  // Delimitadores
  { id: 'line', group: 'Delimitadores', label: 'Línea', color: '#4dd6ff', shape: 'line' },
  { id: 'zone', group: 'Delimitadores', label: 'Zona', color: 'rgba(255,211,77,0.25)', shape: 'zone' },
  { id: 'polygon', group: 'Delimitadores', label: 'Polígono', color: 'rgba(167,139,250,0.22)', shape: 'polygon' },
  { id: 'text', group: 'Delimitadores', label: 'Texto', color: '#ffffff', shape: 'text' },

  // Técnicos
  { id: 'coach', group: 'Técnicos', label: 'Entrenador', color: '#10B981', shape: 'coach' },
  { id: 'staff', group: 'Técnicos', label: 'Cuerpo técnico', color: '#a78bfa', shape: 'coach' },
];

const TOOL_GROUPS = ['Jugadores', 'Equipamiento', 'Porteria', 'Movimientos', 'Delimitadores', 'Técnicos'];

// Shapes que se dibujan centradas en (x, y) → admiten scale y rotation
// como transformación canvas. Las shapes con dos puntos (arrow, line, zone)
// ya tienen orientación implícita en su geometría; los polígonos tienen
// vértices propios.
const PUNCTUAL_SHAPES = new Set([
  'circle', 'ball', 'cone', 'bib', 'mannequin',
  'goal_big', 'goal_small', 'pole', 'coach', 'text',
]);

// Paleta para "Cambiar color" del elemento seleccionado.
const COLOR_PALETTE = [
  '#10B981', // emerald
  '#ff4d6d', // red
  '#ffd34d', // yellow
  '#4dd6ff', // sky
  '#a78bfa', // violet
  '#ff8c00', // orange
  '#ffffff', // white
  '#0f172a', // dark
];

// ============================================================
// FIELD ORIENTATIONS
// ============================================================
// 'full'             → campo entero (legacy, retrocompatible con pizarras viejas).
// 'half-vertical'    → medio campo en vertical (portería arriba, mediocampo abajo).
//                      Pensado para tareas cerca de portería (Ejemplo A del documento).
// 'half-horizontal'  → medio campo apaisado (portería izquierda, mediocampo derecha).
//                      Pensado para organización y explicación de jugadas (Ejemplo B).
const FIELD_DIMENSIONS = {
  'full': { width: 820, height: 520 },
  'half-vertical': { width: 540, height: 620 },
  'half-horizontal': { width: 760, height: 460 },
};

// Medida del lienzo de la pizarra rápida (sesiones Elite). Las coordenadas de
// esas pizarras están guardadas EN ESTE espacio, así que cualquier vista que
// las muestre debe usar el mismo tamaño y el campo entero; si no, los objetos
// salen desplazados y apiñados respecto a donde los puso el entrenador.
const LEGACY_BOARD_SIZE = { width: 700, height: 460 };
const LEGACY_BOARD_ORIENTATION = 'full';

/** ¿La pizarra está en el formato antiguo (elementos con `tool`)? */
function isLegacyBoard(elements) {
  return (elements || []).some(el => el && el.tool && !el.shape);
}

/**
 * Tamaño y orientación con los que hay que PINTAR una pizarra guardada.
 * Devolver esto desde un solo sitio evita que cada pantalla lo adivine a su
 * manera (que es lo que provocaba el descuadre entre el editor y la vista).
 */
function boardViewport(elements, orientation) {
  if (isLegacyBoard(elements)) {
    return { orientation: LEGACY_BOARD_ORIENTATION, dims: LEGACY_BOARD_SIZE };
  }
  const o = orientation || 'half-vertical';
  return { orientation: o, dims: FIELD_DIMENSIONS[o] || FIELD_DIMENSIONS['half-vertical'] };
}

const FIELD_OPTIONS = [
  { value: 'half-vertical', label: 'Medio · vertical', hint: 'Tareas cerca de portería' },
  { value: 'half-horizontal', label: 'Medio · horizontal', hint: 'Explicación de jugadas' },
  { value: 'full', label: 'Campo entero', hint: 'Pizarra clásica' },
];

// ============================================================
// DRAW HELPERS
// ============================================================
// El contexto 2D de <canvas> no interpreta var(--xxx) — fillStyle/strokeStyle
// ignoran silenciosamente cualquier valor que no sea un color CSS válido y
// se quedan con el último color asignado con éxito. Como TOOLS usa
// 'var(--primary)' para "Jugador Local" (para que el selector de color en
// el editor herede el tema), hay que resolverlo al valor real antes de
// dárselo al canvas.
function resolveCanvasColor(color) {
  if (typeof color === 'string' && color.startsWith('var(')) {
    const varName = color.slice(4, -1).trim();
    const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    return resolved || '#10B981'; // fallback razonable si la variable no existe
  }
  return color;
}

function drawElement(ctx, el, selected = false) {
  ctx.save();
  const { x, y, x2, y2, shape, label, text } = el;
  const color = resolveCanvasColor(el.color);

  if (selected) {
    ctx.shadowColor = resolveCanvasColor('var(--primary)');
    ctx.shadowBlur = 8;
  } else if (el.locked) {
    // Halo discreto para que se reconozcan los elementos bloqueados
    ctx.shadowColor = 'rgba(16,185,129,0.55)';
    ctx.shadowBlur = 5;
  }

  // Aplicar scale y rotation sólo a formas puntuales. Las dos-puntos y
  // los polígonos definen orientación con sus propios coordenadas.
  if (PUNCTUAL_SHAPES.has(shape)) {
    const sc = typeof el.scale === 'number' ? el.scale : 1;
    const rot = typeof el.rotation === 'number' ? el.rotation : 0;
    if (sc !== 1 || rot !== 0) {
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.scale(sc, sc);
      ctx.translate(-x, -y);
    }
  }

  switch (shape) {
    case 'circle': // Player
      ctx.fillStyle = color;
      ctx.strokeStyle = 'rgba(255,255,255,0.8)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(x, y, 13, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      if (el.number) {
        ctx.fillStyle = color === '#ffd34d' ? '#000' : '#fff';
        ctx.font = 'bold 9px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(el.number, x, y);
      }
      if (label) {
        ctx.fillStyle = 'rgba(255,255,255,0.75)';
        ctx.font = '7px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(label, x, y + 16);
      }
      break;

    case 'ball':
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      // Pentagon pattern
      ctx.fillStyle = '#333';
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
      break;

    case 'cone':
      ctx.fillStyle = color;
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, y - 12);
      ctx.lineTo(x - 9, y + 8);
      ctx.lineTo(x + 9, y + 8);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // Base line
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x - 9, y + 8);
      ctx.lineTo(x + 9, y + 8);
      ctx.stroke();
      break;

    case 'pole':
      // Pica: vara fina vertical con bandera triangular y base
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, y + 10);
      ctx.lineTo(x, y - 14);
      ctx.stroke();
      // Banderita
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(x, y - 14);
      ctx.lineTo(x + 10, y - 10);
      ctx.lineTo(x, y - 6);
      ctx.closePath();
      ctx.fill();
      // Base
      ctx.fillStyle = 'rgba(255,255,255,0.7)';
      ctx.beginPath();
      ctx.arc(x, y + 10, 3, 0, Math.PI * 2);
      ctx.fill();
      break;

    case 'coach':
      // Entrenador / cuerpo técnico — silueta con tablilla
      ctx.fillStyle = color;
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 1.5;
      // Cabeza
      ctx.beginPath();
      ctx.arc(x, y - 9, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      // Cuerpo
      ctx.beginPath();
      ctx.moveTo(x - 7, y + 12);
      ctx.lineTo(x - 7, y - 3);
      ctx.lineTo(x + 7, y - 3);
      ctx.lineTo(x + 7, y + 12);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      // Tablilla en el brazo
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillRect(x + 5, y - 1, 6, 6);
      ctx.strokeStyle = 'rgba(0,0,0,0.4)';
      ctx.lineWidth = 0.5;
      ctx.strokeRect(x + 5, y - 1, 6, 6);
      // Letra T
      ctx.fillStyle = color;
      ctx.font = 'bold 9px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('T', x, y + 16);
      break;

    case 'bib':
      ctx.fillStyle = color;
      ctx.strokeStyle = 'rgba(255,255,255,0.6)';
      ctx.lineWidth = 1.5;
      // Body
      ctx.beginPath();
      ctx.roundRect ? ctx.roundRect(x - 9, y - 11, 18, 22, 3) :
        ctx.rect(x - 9, y - 11, 18, 22);
      ctx.fill();
      ctx.stroke();
      // P letter
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.font = 'bold 10px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('P', x, y);
      break;

    case 'mannequin':
      ctx.strokeStyle = color;
      ctx.fillStyle = color;
      ctx.lineWidth = 2;
      // Head
      ctx.beginPath();
      ctx.arc(x, y - 14, 5, 0, Math.PI * 2);
      ctx.fill();
      // Body
      ctx.beginPath();
      ctx.moveTo(x, y - 9);
      ctx.lineTo(x, y + 5);
      ctx.stroke();
      // Arms
      ctx.beginPath();
      ctx.moveTo(x - 8, y - 4);
      ctx.lineTo(x + 8, y - 4);
      ctx.stroke();
      // Legs
      ctx.beginPath();
      ctx.moveTo(x, y + 5);
      ctx.lineTo(x - 6, y + 16);
      ctx.moveTo(x, y + 5);
      ctx.lineTo(x + 6, y + 16);
      ctx.stroke();
      break;

    case 'goal_big':
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(x - 24, y + 8);
      ctx.lineTo(x - 24, y - 12);
      ctx.lineTo(x + 24, y - 12);
      ctx.lineTo(x + 24, y + 8);
      ctx.stroke();
      // Net lines
      ctx.lineWidth = 0.5;
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      for (let i = -16; i <= 16; i += 8) {
        ctx.beginPath();
        ctx.moveTo(x + i, y - 12);
        ctx.lineTo(x + i, y + 8);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.moveTo(x - 24, y);
      ctx.lineTo(x + 24, y);
      ctx.stroke();
      break;

    case 'goal_small':
      ctx.strokeStyle = color;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(x - 14, y + 6);
      ctx.lineTo(x - 14, y - 8);
      ctx.lineTo(x + 14, y - 8);
      ctx.lineTo(x + 14, y + 6);
      ctx.stroke();
      break;

    case 'arrow':
    case 'arrow_dashed': {
      // Pase (sólida) y desmarque (punteada) — comparten geometría
      if (x2 === undefined) break;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2.5;
      ctx.setLineDash(shape === 'arrow_dashed' ? [6, 4] : []);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.setLineDash([]);
      const angle = Math.atan2(y2 - y, x2 - x);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - 12 * Math.cos(angle - 0.4), y2 - 12 * Math.sin(angle - 0.4));
      ctx.lineTo(x2 - 12 * Math.cos(angle + 0.4), y2 - 12 * Math.sin(angle + 0.4));
      ctx.closePath();
      ctx.fill();
      break;
    }

    case 'arrow_dribble': {
      // Conducción — línea ondulada con punta
      if (x2 === undefined) break;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2.5;
      ctx.setLineDash([]);
      const dxD = x2 - x;
      const dyD = y2 - y;
      const len = Math.sqrt(dxD * dxD + dyD * dyD);
      if (len < 1) break;
      const nx = -dyD / len; // perpendicular
      const ny = dxD / len;
      const amplitude = 6;
      const wavelength = 24;
      const cycles = Math.max(2, Math.floor(len / wavelength));
      ctx.beginPath();
      ctx.moveTo(x, y);
      for (let i = 1; i <= cycles * 8; i++) {
        const t = i / (cycles * 8);
        const px = x + dxD * t;
        const py = y + dyD * t;
        const wave = Math.sin(t * cycles * Math.PI * 2) * amplitude;
        ctx.lineTo(px + nx * wave, py + ny * wave);
      }
      ctx.stroke();
      // Cabeza de la flecha al final
      const angD = Math.atan2(dyD, dxD);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - 12 * Math.cos(angD - 0.4), y2 - 12 * Math.sin(angD - 0.4));
      ctx.lineTo(x2 - 12 * Math.cos(angD + 0.4), y2 - 12 * Math.sin(angD + 0.4));
      ctx.closePath();
      ctx.fill();
      break;
    }

    case 'arrow_shot': {
      // Tiro — trazo grueso con doble cabeza para enfatizar potencia
      if (x2 === undefined) break;
      ctx.strokeStyle = color;
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.lineCap = 'butt';
      const angS = Math.atan2(y2 - y, x2 - x);
      ctx.fillStyle = color;
      // Cabeza principal
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - 16 * Math.cos(angS - 0.4), y2 - 16 * Math.sin(angS - 0.4));
      ctx.lineTo(x2 - 16 * Math.cos(angS + 0.4), y2 - 16 * Math.sin(angS + 0.4));
      ctx.closePath();
      ctx.fill();
      // Segunda cabeza desplazada hacia atrás (efecto "doble")
      const back = 8;
      const bx = x2 - back * Math.cos(angS);
      const by = y2 - back * Math.sin(angS);
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx - 12 * Math.cos(angS - 0.45), by - 12 * Math.sin(angS - 0.45));
      ctx.lineTo(bx - 12 * Math.cos(angS + 0.45), by - 12 * Math.sin(angS + 0.45));
      ctx.closePath();
      ctx.fill();
      break;
    }

    case 'line':
      if (x2 === undefined) break;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      break;

    case 'zone': {
      if (x2 === undefined) break;
      ctx.fillStyle = color;
      ctx.strokeStyle = color.replace('0.25', '0.7');
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 3]);
      const zx = Math.min(x, x2), zy = Math.min(y, y2);
      const zw = Math.abs(x2 - x), zh = Math.abs(y2 - y);
      ctx.fillRect(zx, zy, zw, zh);
      ctx.strokeRect(zx, zy, zw, zh);
      ctx.setLineDash([]);
      break;
    }

    case 'polygon': {
      // Polígono libre con N vértices. Si solo hay 1 punto, se dibuja un
      // marcador; si hay 2, una línea; si hay ≥3 se cierra y rellena.
      const pts = el.points || [];
      if (pts.length < 1) break;
      if (pts.length >= 2) {
        ctx.fillStyle = color;
        const strokeCol = typeof color === 'string'
          ? color.replace(/,[\d.]+\)$/, ',0.75)')
          : color;
        ctx.strokeStyle = strokeCol;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([5, 3]);
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
        if (pts.length >= 3 && el.closed !== false) {
          ctx.closePath();
          ctx.fill();
        }
        ctx.stroke();
        ctx.setLineDash([]);
      }
      // Vértices visibles
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.lineWidth = 1;
      pts.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      });
      break;
    }

    case 'text':
      ctx.fillStyle = color;
      ctx.font = 'bold 13px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(text || 'Texto', x, y);
      break;

    default: break;
  }
  ctx.restore();
}

function drawField(ctx, w, h, orientation = 'full') {
  // Grass base + stripes (sirve para cualquier orientación)
  ctx.fillStyle = '#1a7a3a';
  ctx.fillRect(0, 0, w, h);
  const sc = 12;
  for (let i = 0; i < sc; i++) {
    ctx.fillStyle = i % 2 === 0 ? '#1d8240' : '#178035';
    ctx.fillRect(0, i * h / sc, w, h / sc);
  }

  const lc = 'rgba(255,255,255,0.55)';
  const lw = 1.5;
  const mg = 18;
  ctx.strokeStyle = lc;
  ctx.lineWidth = lw;

  if (orientation === 'half-vertical') {
    drawHalfFieldVertical(ctx, w, h, { lc, mg });
    return;
  }
  if (orientation === 'half-horizontal') {
    drawHalfFieldHorizontal(ctx, w, h, { lc, mg });
    return;
  }

  // === FULL FIELD (legacy) ===
  // Outer
  ctx.strokeRect(mg, mg, w - mg * 2, h - mg * 2);
  // Halfway
  ctx.beginPath(); ctx.moveTo(mg, h / 2); ctx.lineTo(w - mg, h / 2); ctx.stroke();
  // Centre circle
  ctx.beginPath(); ctx.arc(w / 2, h / 2, Math.min(w, h) * 0.12, 0, Math.PI * 2); ctx.stroke();
  ctx.fillStyle = lc; ctx.beginPath(); ctx.arc(w / 2, h / 2, 3, 0, Math.PI * 2); ctx.fill();
  // Penalty areas
  const pa_w = w * 0.44, pa_h = h * 0.17;
  ctx.strokeRect((w - pa_w) / 2, mg, pa_w, pa_h);
  ctx.strokeRect((w - pa_w) / 2, h - mg - pa_h, pa_w, pa_h);
  // Goal areas
  const ga_w = w * 0.22, ga_h = h * 0.08;
  ctx.strokeRect((w - ga_w) / 2, mg, ga_w, ga_h);
  ctx.strokeRect((w - ga_w) / 2, h - mg - ga_h, ga_w, ga_h);
  // Goals
  const goal_w = w * 0.12, goal_d = h * 0.022;
  ctx.lineWidth = 3;
  ctx.strokeRect((w - goal_w) / 2, mg - goal_d, goal_w, goal_d);
  ctx.strokeRect((w - goal_w) / 2, h - mg, goal_w, goal_d);
  // Penalty spots + arcs ("D") que sobresalen por delante de cada área
  const topSpot = mg + pa_h * 0.62;
  const botSpot = h - mg - pa_h * 0.62;
  ctx.fillStyle = lc;
  [topSpot, botSpot].forEach(py => {
    ctx.beginPath(); ctx.arc(w / 2, py, 2.5, 0, Math.PI * 2); ctx.fill();
  });
  ctx.lineWidth = lw;
  // Arco superior (bulto hacia abajo/centro)
  {
    const d = (mg + pa_h) - topSpot;
    const R = d / 0.62;
    const a = Math.asin(Math.min(0.999, d / R));
    ctx.beginPath(); ctx.arc(w / 2, topSpot, R, a, Math.PI - a); ctx.stroke();
  }
  // Arco inferior (bulto hacia arriba/centro)
  {
    const d = botSpot - (h - mg - pa_h);
    const R = d / 0.62;
    const a = Math.asin(Math.min(0.999, d / R));
    ctx.beginPath(); ctx.arc(w / 2, botSpot, R, Math.PI + a, 2 * Math.PI - a); ctx.stroke();
  }
}

// Medio campo en vertical: portería arriba, área superior, círculo central
// asomando por el borde inferior. Línea inferior representa la línea del medio
// del campo (sin cerrar el rectángulo perimetral por abajo para sugerir continuidad).
function drawHalfFieldVertical(ctx, w, h, { lc, mg }) {
  // Outer (cerrado en 3 lados, mediocampo abajo es línea normal)
  ctx.beginPath();
  ctx.moveTo(mg, h - mg);
  ctx.lineTo(mg, mg);
  ctx.lineTo(w - mg, mg);
  ctx.lineTo(w - mg, h - mg);
  ctx.stroke();
  // Halfway line (borde inferior)
  ctx.beginPath();
  ctx.moveTo(mg, h - mg);
  ctx.lineTo(w - mg, h - mg);
  ctx.stroke();

  // Penalty area (arriba, más grande)
  const pa_w = w * 0.62, pa_h = h * 0.30;
  ctx.strokeRect((w - pa_w) / 2, mg, pa_w, pa_h);
  // Goal area (arriba, más pequeña)
  const ga_w = w * 0.30, ga_h = h * 0.13;
  ctx.strokeRect((w - ga_w) / 2, mg, ga_w, ga_h);
  // Goal mouth
  const goal_w = w * 0.18, goal_d = h * 0.025;
  ctx.lineWidth = 3;
  ctx.strokeRect((w - goal_w) / 2, mg - goal_d, goal_w, goal_d);
  ctx.lineWidth = 1.5;
  // Penalty spot
  const spotY = mg + pa_h * 0.62;
  ctx.fillStyle = lc;
  ctx.beginPath();
  ctx.arc(w / 2, spotY, 2.5, 0, Math.PI * 2);
  ctx.fill();
  // Penalty arc: la "D" debe salir por DELANTE del área (hacia el centro).
  // Radio calculado desde el punto de penalti para que el arco cruce la línea
  // frontal del área y sobresalga; sólo se dibuja el tramo visible fuera del área.
  {
    const areaFront = mg + pa_h;          // línea frontal del área
    const d = areaFront - spotY;          // distancia penalti → línea del área
    const R = d / 0.62;                    // radio para que sobresalga
    const a = Math.asin(Math.min(0.999, d / R));
    ctx.beginPath();
    ctx.arc(w / 2, spotY, R, a, Math.PI - a);  // bulto inferior (hacia el centro)
    ctx.stroke();
  }

  // Centre circle asomando por el borde inferior (mediocampo)
  const cr = Math.min(w, h) * 0.13;
  ctx.beginPath();
  ctx.arc(w / 2, h - mg, cr, Math.PI, 2 * Math.PI);
  ctx.stroke();
  ctx.fillStyle = lc;
  ctx.beginPath();
  ctx.arc(w / 2, h - mg, 3, 0, Math.PI * 2);
  ctx.fill();
}

// Medio campo apaisado: portería a la izquierda, área izquierda, círculo central
// asomando por el borde derecho. La línea derecha representa el medio del campo.
function drawHalfFieldHorizontal(ctx, w, h, { lc, mg }) {
  // Outer (cerrado por arriba/abajo/izquierda; derecha es la línea de mediocampo)
  ctx.beginPath();
  ctx.moveTo(w - mg, mg);
  ctx.lineTo(mg, mg);
  ctx.lineTo(mg, h - mg);
  ctx.lineTo(w - mg, h - mg);
  ctx.stroke();
  // Halfway (lado derecho)
  ctx.beginPath();
  ctx.moveTo(w - mg, mg);
  ctx.lineTo(w - mg, h - mg);
  ctx.stroke();

  // Penalty area (izquierda)
  const pa_h = h * 0.62, pa_w = w * 0.30;
  ctx.strokeRect(mg, (h - pa_h) / 2, pa_w, pa_h);
  // Goal area (izquierda)
  const ga_h = h * 0.30, ga_w = w * 0.13;
  ctx.strokeRect(mg, (h - ga_h) / 2, ga_w, ga_h);
  // Goal mouth
  const goal_h = h * 0.18, goal_d = w * 0.025;
  ctx.lineWidth = 3;
  ctx.strokeRect(mg - goal_d, (h - goal_h) / 2, goal_d, goal_h);
  ctx.lineWidth = 1.5;
  // Penalty spot
  const spotX = mg + pa_w * 0.62;
  ctx.fillStyle = lc;
  ctx.beginPath();
  ctx.arc(spotX, h / 2, 2.5, 0, Math.PI * 2);
  ctx.fill();
  // Penalty arc: la "D" sobresale por delante del área (hacia la derecha).
  {
    const areaFront = mg + pa_w;
    const d = areaFront - spotX;
    const R = d / 0.62;
    const b = Math.acos(Math.min(0.999, d / R));
    ctx.beginPath();
    ctx.arc(spotX, h / 2, R, -b, b);  // bulto derecho (hacia el centro)
    ctx.stroke();
  }

  // Centre circle asomando por el borde derecho
  const cr = Math.min(w, h) * 0.13;
  ctx.beginPath();
  ctx.arc(w - mg, h / 2, cr, 0.5 * Math.PI, 1.5 * Math.PI);
  ctx.stroke();
  ctx.fillStyle = lc;
  ctx.beginPath();
  ctx.arc(w - mg, h / 2, 3, 0, Math.PI * 2);
  ctx.fill();
}

// ============================================================
// LEGACY FORMAT ADAPTER
// ============================================================
// Formatos históricos de pizarra que coexisten en la base de datos:
//  - Actual (BoardCanvas.js): { shape, color, x, y, ... }
//  - Legado (TacticalBoardWithSteps.js, sesiones Elite): { tool, x, y, ... }
// Cualquier vista que solo sepa dibujar `shape` (drawElement de este
// archivo) debe pasar los elementos por aquí antes de dibujarlos, o los
// elementos legado no se pintarán.
const LEGACY_TOOL_TO_SHAPE = {
  player_home:    { shape: 'circle', color: 'var(--primary)' },
  player_away:    { shape: 'circle', color: '#ff4d6d' },
  player_neutral: { shape: 'circle', color: '#ffd34d' },
  cone:           { shape: 'cone',   color: '#ff8c00' },
  ball:           { shape: 'ball',   color: '#ffffff' },
  arrow:          { shape: 'arrow',  color: '#ffd34d' },
  dashed_arrow:   { shape: 'arrow',  color: '#4dd6ff' },
  line:           { shape: 'line',   color: '#a78bfa' },
  text:           { shape: 'text',   color: '#ffffff' },
  // Materiales de la pizarra rápida (sesiones Elite): mismos dibujos que la pro.
  goal_small:     { shape: 'goal_small', color: '#ffffff' },
  goal_big:       { shape: 'goal_big',   color: '#ffffff' },
  pole:           { shape: 'pole',       color: '#ffd34d' },
  mannequin:      { shape: 'mannequin',  color: '#a78bfa' },
  bib:            { shape: 'bib',        color: '#4dd6ff' },
};

function normalizeBoardElements(elements) {
  return (elements || []).map((el, i) => {
    if (el.shape) return el; // ya en formato nuevo
    const mapped = LEGACY_TOOL_TO_SHAPE[el.tool] || { shape: 'circle', color: '#ffffff' };
    return {
      id: el.id ?? i,
      x: el.x, y: el.y,
      ...(el.x2 !== undefined ? { x2: el.x2, y2: el.y2 } : {}),
      label: el.label,
      text: el.text,
      // El giro y el tamaño se conservan: si no, un objeto girado en el editor
      // aparecería recto en el enlace compartido y en el PDF.
      ...(el.rotation ? { rotation: el.rotation } : {}),
      ...(el.scale ? { scale: el.scale } : {}),
      ...mapped,
    };
  });
}

export {
  TOOLS,
  resolveCanvasColor,
  TOOL_GROUPS,
  PUNCTUAL_SHAPES,
  COLOR_PALETTE,
  FIELD_DIMENSIONS,
  FIELD_OPTIONS,
  LEGACY_BOARD_SIZE,
  isLegacyBoard,
  boardViewport,
  drawElement,
  drawField,
  normalizeBoardElements,
};
